import { Slot } from '@radix-ui/react-slot';
import { type Spacings } from '@smart/web-foundations';
import classnames from 'classnames';
import React from 'react';
import type { ExtendWithCommonProps } from '../../types';
import { type Display, type ResponsiveProperties } from '../../types';
import { joinClasses, responsiveClasses } from '../../utils';
import { Layout, type LayoutProps } from '../layout/layout';
import scss from './flex.module.scss';

export const directions = [
  'row',
  'row-reverse',
  'column',
  'column-reverse',
] as const;
export type Directions = (typeof directions)[number];

export const wrap = ['nowrap', 'wrap', 'wrap-reverse'] as const;
export type Wrap = (typeof wrap)[number];

export const justifyContent = [
  'start',
  'end',
  'center',
  'space-around',
  'space-between',
  'space-evenly',
] as const;
export type JustifyContent = (typeof justifyContent)[number];

export const alignItems = [
  'start',
  'end',
  'center',
  'stretch',
  'baseline',
] as const;
export type AlignItems = (typeof alignItems)[number];

export const alignContent = [
  'start',
  'end',
  'center',
  'stretch',
  'space-around',
  'space-between',
] as const;
export type AlignContent = (typeof alignContent)[number];

export type FlexProps = ExtendWithCommonProps<
  LayoutProps,
  {
    /** Type of flex container.
     * @default 'flex'
     */
    display?: ResponsiveProperties<
      Extract<Display, 'flex' | 'inline-flex' | 'none'>
    >;
    /** Establishes main axis and the direction items are placed inside the container.*/
    direction?: ResponsiveProperties<Directions>;
    /** Defines whether items should wrap or not.*/
    wrap?: ResponsiveProperties<Wrap>;
    /** Defines alignment of items along the main axis.*/
    justifyContent?: ResponsiveProperties<JustifyContent>;

    /** Defines alignment of items along the cross axis.
     */
    alignItems?: ResponsiveProperties<AlignItems>;
    /** Defines alignment of lines along the cross axis. Only works if there are multiple lines of content (wrap is not 'nowrap').
     */
    alignContent?: ResponsiveProperties<AlignContent>;
    /** Space between items. */
    gap?: Spacings;
    /** Space between rows. */
    rowGap?: Spacings;
    /** Space between columns. */
    columnGap?: Spacings;
    asChild?: boolean;
    /** Component's children. */
    children: React.ReactNode;
    role?: string;
  }
>;

/**
 * Creates a flex container that enables a flex context for its children.
 *
 * @link https://prod.web-components.srv.smart.com/?path=/docs/layout-flex--docs
 */
export const Flex = React.forwardRef<HTMLDivElement, FlexProps>(
  (
    {
      display = 'flex',
      direction,
      wrap,
      justifyContent,
      alignItems,
      alignContent,
      gap,
      rowGap,
      columnGap,
      asChild,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const classes = classnames(
      joinClasses(responsiveClasses('flex-direction', direction), scss),
      joinClasses(responsiveClasses('flex-wrap', wrap), scss),
      joinClasses(responsiveClasses('justify-content', justifyContent), scss),
      joinClasses(responsiveClasses('align-items', alignItems), scss),
      joinClasses(responsiveClasses('align-content', alignContent), scss),
      {
        [scss[`gap-${gap}`]]: gap,
        [scss[`row-gap-${rowGap}`]]: rowGap,
        [scss[`column-gap-${columnGap}`]]: columnGap,
      },
      className,
    );

    const Tag = asChild ? Slot : 'div';

    return (
      <Layout
        className={classes}
        {...props}
        display={display}
        ref={ref}
        asChild
      >
        <Tag>{children}</Tag>
      </Layout>
    );
  },
);

Flex.displayName = 'Flex';
